import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Pause, Play, Sparkles, Stethoscope, Wrench } from 'lucide-react';
import { api } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { Badge, ReachDot, EmptyState, ErrorState, Skeleton, Spinner, useFetch, useToast } from '../components/ui';
import { TimeseriesChart } from '../components/charts';

/* ================================================================== */
/* Types — mirrors plugins/routers/src/loadbalancer.ts, lb-actions.ts  */
/* and lb-diagnostics.ts                                               */
/* ================================================================== */

interface LbWan {
  name: string;
  running: boolean;
  disabled: boolean;
  slow: boolean;
  rxBytes: number;
  txBytes: number;
  dhcpStatus: string | null;
  address: string | null;
  gateway: string | null;
  comment: string;
  speedKbps: number | null;
  slotsCarried: number;
}

interface LbSlot {
  mark: string;
  classifier: string;
  disabled: boolean;
  routingTable: string | null;
  egress: string | null;
  egressGateway: string | null;
  connections: number;
}

interface LbStatus {
  ts: number;
  identity: string | null;
  version: string | null;
  uptime: string | null;
  cpuLoad: number | null;
  pccMode: string | null;
  wans: LbWan[];
  slots: LbSlot[];
  mainEgress: string[];
  connectionsTotal: number;
  wansTotal: number;
  wansUp: number;
  wansSlow: number;
  wansCarrying: number;
}

type LbAction = 'enable-slot' | 'disable-slot' | 'enable-line' | 'disable-line' | 'renew-dhcp' | 'restart-port' | 'flush-dns' | 'recalibrate-line';

interface LbPing {
  host: string;
  sent: number;
  received: number;
  lossPct: number;
  avgMs: number | null;
  minMs: number | null;
  maxMs: number | null;
  jitterMs: number | null;
}

interface LbLineDiag {
  name: string;
  gateway: LbPing | null;
  internet: LbPing | null;
  linkDowns: number;
  lastLinkDown: string | null;
  lastLinkDownAgoSec: number | null;
  rxErrors: number;
  txErrors: number;
  rxDrops: number;
  txDrops: number;
  rate: string | null;
  fullDuplex: boolean | null;
}

interface LbFix {
  action: LbAction;
  target: string;
  label: string;
  confirm: string;
}

interface LbFinding {
  id: string;
  severity: 'critical' | 'warning' | 'info';
  scope: string;
  title: string;
  detail: string;
  fixes: LbFix[];
}

interface AiLbFix extends LbFix {
  why: string;
}
interface AiLbFixes {
  summary: string;
  fixes: AiLbFix[];
}

interface LbDiagnosis {
  ts: number;
  verdict: 'healthy' | 'unstable' | 'faulty';
  summary: string;
  lines: LbLineDiag[];
  dnsOk: boolean | null;
  findings: LbFinding[];
  status: LbStatus;
}

const SEVERITY_INTENT = { critical: 'bad', warning: 'warn', info: 'info' } as const;
const VERDICT = {
  healthy: { intent: 'good', label: 'healthy' },
  unstable: { intent: 'warn', label: 'unstable' },
  faulty: { intent: 'bad', label: 'faulty' },
} as const;

const REFRESH_OPTIONS = [
  { sec: 2, label: '2s' },
  { sec: 5, label: '5s' },
  { sec: 10, label: '10s' },
  { sec: 30, label: '30s' },
  { sec: 60, label: '1 min' },
];
const WINDOW_OPTIONS = [
  { min: 15, label: '15 min' },
  { min: 60, label: '1 hr' },
];

const PCC_MODE_HELP: Record<string, string> = {
  'both-addresses': 'same source + destination stays on one line; other pairs may use another',
  'src-address': 'each user stays on one line for everything',
  'dst-address': 'each destination stays on one line',
  'both-addresses-and-ports': 'every connection hashed separately',
};

/** Mirrors `lineLockReason` on the server, which enforces it; here it only explains a greyed-out button. */
function lineLock(status: LbStatus, line: string): string | null {
  if (status.mainEgress.includes(line)) {
    return "Carries the router's own traffic, including the VPN it is managed through — switch it off on the router itself";
  }
  if (!status.wans.some((w) => w.name !== line && w.running && !w.disabled)) return 'The only line still working';
  return null;
}

function lossIntent(ping: LbPing): 'good' | 'warn' | 'bad' {
  return ping.lossPct >= 30 ? 'bad' : ping.lossPct > 0 ? 'warn' : 'good';
}

function fmtMs(ms: number | null): string {
  return ms === null ? '—' : `${ms < 10 ? ms.toFixed(1) : Math.round(ms)} ms`;
}

function fmtBps(bps: number | null): string {
  if (bps === null) return '—';
  const units = ['bps', 'Kbps', 'Mbps', 'Gbps'];
  let v = bps;
  let i = 0;
  while (v >= 1000 && i < units.length - 1) {
    v /= 1000;
    i += 1;
  }
  return `${v.toFixed(i === 0 ? 0 : 1)} ${units[i]}`;
}

/** Throughput from two consecutive readings of the byte counters; null on the first sample or a counter reset. */
function rate(prev: LbStatus | undefined, curr: LbStatus, wan: string, key: 'rxBytes' | 'txBytes'): number | null {
  const a = prev?.wans.find((w) => w.name === wan);
  const b = curr.wans.find((w) => w.name === wan);
  if (!a || !b || !prev) return null;
  const sec = (curr.ts - prev.ts) / 1000;
  const delta = b[key] - a[key];
  return sec > 0 && delta >= 0 ? (delta * 8) / sec : null;
}

/* ================================================================== */
/* Page                                                                */
/* ================================================================== */

export function LoadBalancerMonitor() {
  const { id } = useParams<{ id: string }>();
  const { can } = useAuth();
  const toast = useToast();
  const canWrite = can('resource:write');

  /** Key of the change in flight (`action:target`) — one at a time, the router is a single slow SSH login away. */
  const [busy, setBusy] = useState<string | null>(null);
  const [diagnosis, setDiagnosis] = useState<LbDiagnosis | null>(null);
  const [diagnosing, setDiagnosing] = useState(false);
  const [diagError, setDiagError] = useState<string | null>(null);
  /** Something was changed after the test ran, so its findings may no longer hold. */
  const [diagStale, setDiagStale] = useState(false);
  const [aiFixes, setAiFixes] = useState<AiLbFixes | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);

  const [refreshSec, setRefreshSec] = useState(5);
  const [windowMin, setWindowMin] = useState(15);
  const [paused, setPaused] = useState(false);

  const [samples, setSamples] = useState<LbStatus[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const { data: resource } = useFetch(() => api.get<{ id: string; name: string; kind: string; health: string }>(`/api/resources/${id}`), [id]);

  const maxPoints = Math.max(10, Math.ceil((windowMin * 60) / refreshSec));

  useEffect(() => {
    if (!id || paused) return;
    let cancelled = false;

    let timer: number | undefined;

    // Chained rather than on an interval: every poll is a fresh SSH login, so a
    // slow one must not let the next pile up behind it.
    const tick = async () => {
      try {
        const status = await api.get<LbStatus>(`/api/plugins/routers/lb/${id}/status`);
        if (cancelled) return;
        setError(null);
        setLoading(false);
        setSamples((current) => [...current, status].slice(-maxPoints));
      } catch (err) {
        if (cancelled) return;
        setLoading(false);
        // Keep the last good sample on screen: these routers sit behind site
        // VPNs that drop the odd SSH login, and blanking the page for one
        // missed poll reads as an outage that isn't happening.
        setError(err instanceof Error ? err.message : 'Could not reach the load balancer');
      }
      if (!cancelled) timer = window.setTimeout(() => void tick(), refreshSec * 1000);
    };

    void tick();
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [id, refreshSec, paused, maxPoints]);

  const act = async (action: LbAction, target: string, question: string) => {
    if (!id || busy) return;
    if (!confirm(question)) return;
    setBusy(`${action}:${target}`);
    try {
      const result = await api.post<{ message: string; status: LbStatus }>(`/api/plugins/routers/lb/${id}/action`, {
        action,
        target,
        confirmed: true,
      });
      toast(result.message, 'good');
      setError(null);
      setSamples((current) => [...current, result.status].slice(-maxPoints));
      if (diagnosis) setDiagStale(true);
    } catch (err) {
      toast('Change failed', 'bad', err instanceof Error ? err.message : undefined);
    } finally {
      setBusy(null);
    }
  };

  const diagnose = async () => {
    if (!id || diagnosing) return;
    setDiagnosing(true);
    setDiagError(null);
    try {
      const result = await api.post<LbDiagnosis>(`/api/plugins/routers/lb/${id}/diagnose`, {});
      setDiagnosis(result);
      setAiFixes(null);
      setAiError(null);
      setDiagStale(false);
      setSamples((current) => [...current, result.status].slice(-maxPoints));
    } catch (err) {
      setDiagError(err instanceof Error ? err.message : 'The test did not finish');
    } finally {
      setDiagnosing(false);
    }
  };

  const suggestWithAi = async () => {
    if (!id || !diagnosis || aiLoading) return;
    setAiLoading(true);
    setAiError(null);
    try {
      setAiFixes(await api.post<AiLbFixes>('/api/ai/lb-fixes', { resourceId: id, diagnosis }));
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'The AI could not suggest fixes');
    } finally {
      setAiLoading(false);
    }
  };

  const latest = samples[samples.length - 1];
  const previous = samples[samples.length - 2];

  if (loading && samples.length === 0 && !error) {
    return (
      <div className="col" style={{ gap: 14 }}>
        <Skeleton height={90} />
        <Skeleton height={320} />
      </div>
    );
  }
  if (error && samples.length === 0) return <ErrorState error={error} onRetry={() => setError(null)} />;
  if (!latest) return <EmptyState title="No data yet" text="Waiting for the first reading from the load balancer." />;

  const totalSlotConns = latest.slots.reduce((sum, s) => sum + s.connections, 0);
  const carryingNames = latest.wans.filter((w) => w.slotsCarried > 0).map((w) => w.name);

  const rxSeries = latest.wans.map((w) => ({
    name: w.name,
    points: samples.map((s, i) => [s.ts, rate(samples[i - 1], s, w.name, 'rxBytes')] as [number, number | null]),
  }));

  return (
    <div className="col" style={{ gap: 14 }}>
      <div className="page-header">
        <div className="grow">
          <div className="page-subtitle" style={{ marginBottom: 2 }}>
            <Link to={`/resources/${id}`}>
              {resource && <ReachDot health={resource.health} />}
              {resource?.name ?? 'Resource'}
            </Link>
          </div>
          <h1 className="page-title">Load Balancing</h1>
          <div className="page-subtitle">
            {error ? (
              <span style={{ color: 'var(--itr-critical)' }}>{error} — showing last good reading</span>
            ) : (
              <>
                <span className="health" data-health="up" style={{ ['--health-color' as string]: 'var(--itr-up)' }}>
                  <span className="health-dot" />
                </span>{' '}
                live — {new Date(latest.ts).toLocaleTimeString()}
                {latest.identity && ` · ${latest.identity}`}
                {latest.version && ` · RouterOS ${latest.version}`}
                {latest.uptime && ` · up ${latest.uptime}`}
              </>
            )}
          </div>
        </div>
        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
          <label className="row" style={{ gap: 5, fontSize: 12.5 }}>
            Refresh
            <select className="input" style={{ padding: '4px 8px' }} value={refreshSec} onChange={(e) => setRefreshSec(Number(e.target.value))}>
              {REFRESH_OPTIONS.map((o) => (
                <option key={o.sec} value={o.sec}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <label className="row" style={{ gap: 5, fontSize: 12.5 }}>
            Window
            <select className="input" style={{ padding: '4px 8px' }} value={windowMin} onChange={(e) => setWindowMin(Number(e.target.value))}>
              {WINDOW_OPTIONS.map((o) => (
                <option key={o.min} value={o.min}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
          <button className="btn btn-sm" onClick={() => setPaused((p) => !p)}>
            {paused ? (
              <>
                <Play size={13} /> Resume
              </>
            ) : (
              <>
                <Pause size={13} /> Pause
              </>
            )}
          </button>
        </div>
      </div>

      {latest.wansTotal > 1 && latest.wansCarrying < latest.wansTotal && (
        <div className="card" style={{ padding: '10px 14px', fontSize: 13, borderLeft: '3px solid var(--itr-warning)' }}>
          Only {latest.wansCarrying === 0 ? 'no line is' : `${carryingNames.join(', ')} ${latest.wansCarrying === 1 ? 'is' : 'are'}`} carrying
          traffic. Connections are still spread over {latest.slots.length} PCC slots, but slots whose own line is down or
          pulled out fail over and leave through the lines above.
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <span className="card-title">Live traffic</span>
          <span className="subtle" style={{ fontSize: 12 }}>
            every {refreshSec}s · {paused ? 'paused' : 'live'}
          </span>
        </div>
        <div className="col" style={{ padding: '4px 14px 10px', gap: 8 }}>
          {(() => {
            const rows = latest.wans.map((w) => ({
              w,
              down: rate(previous, latest, w.name, 'rxBytes'),
              up: rate(previous, latest, w.name, 'txBytes'),
            }));
            const peak = Math.max(1, ...rows.map((r) => Math.max(r.down ?? 0, r.up ?? 0)));
            const bar = (v: number | null, color: string) => (
              <div style={{ height: 6, borderRadius: 3, background: 'var(--itr-border)', overflow: 'hidden' }}>
                <div style={{ width: `${Math.min(100, ((v ?? 0) / peak) * 100)}%`, height: '100%', background: color, transition: 'width .4s' }} />
              </div>
            );
            return rows.map(({ w, down, up }) => (
              <div key={w.name} className="row" style={{ gap: 12, alignItems: 'center', opacity: w.running ? 1 : 0.6 }}>
                <div className="mono" style={{ width: 80, flexShrink: 0 }}>{w.name}</div>
                <div style={{ width: 120, flexShrink: 0 }}>
                  {w.disabled ? (
                    <Badge intent="warn">taken out</Badge>
                  ) : !w.running ? (
                    <Badge intent="bad">down</Badge>
                  ) : w.slow ? (
                    <Badge intent="warn">slow</Badge>
                  ) : w.slotsCarried > 0 ? (
                    <Badge intent="good">in use</Badge>
                  ) : (
                    <Badge intent="neutral">idle</Badge>
                  )}
                </div>
                <div className="grow" style={{ minWidth: 0 }}>
                  <div className="row" style={{ gap: 8, fontSize: 12.5 }}>
                    <span style={{ width: 22 }} className="subtle">↓</span>
                    <div className="grow">{bar(down, 'var(--itr-up)')}</div>
                    <span className="nums" style={{ width: 90, textAlign: 'right' }}>{fmtBps(down)}</span>
                  </div>
                  <div className="row" style={{ gap: 8, fontSize: 12.5, marginTop: 3 }}>
                    <span style={{ width: 22 }} className="subtle">↑</span>
                    <div className="grow">{bar(up, 'var(--itr-info, var(--itr-up))')}</div>
                    <span className="nums" style={{ width: 90, textAlign: 'right' }}>{fmtBps(up)}</span>
                  </div>
                </div>
                <div className="nums subtle" style={{ width: 90, textAlign: 'right', fontSize: 12 }}>{w.slotsCarried} slot{w.slotsCarried === 1 ? '' : 's'}</div>
              </div>
            ));
          })()}
        </div>
      </div>

      <div className="stat-strip">
        <div className="stat-chip">
          <div className="stat-chip-label">Lines in use</div>
          <div
            className="stat-chip-value"
            style={{ color: latest.wansCarrying < latest.wansTotal ? 'var(--itr-warning)' : 'var(--itr-up)' }}
          >
            {latest.wansCarrying}
          </div>
          <div className="stat-chip-sub">of {latest.wansTotal} lines carry traffic</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Link up</div>
          <div className="stat-chip-value" style={{ color: latest.wansUp < latest.wansTotal ? 'var(--itr-critical)' : undefined }}>
            {latest.wansUp}
          </div>
          <div className="stat-chip-sub">of {latest.wansTotal} ports have a link</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Pulled out as slow</div>
          <div className="stat-chip-value" style={{ color: latest.wansSlow > 0 ? 'var(--itr-warning)' : undefined }}>
            {latest.wansSlow}
          </div>
          <div className="stat-chip-sub">by the router's own speed test</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">Connections</div>
          <div className="stat-chip-value">{latest.connectionsTotal.toLocaleString()}</div>
          <div className="stat-chip-sub">{totalSlotConns.toLocaleString()} balanced by PCC</div>
        </div>
        <div className="stat-chip">
          <div className="stat-chip-label">PCC mode</div>
          <div className="stat-chip-value" style={{ fontSize: 16 }}>
            {latest.pccMode ?? '—'}
          </div>
          <div className="stat-chip-sub">{(latest.pccMode && PCC_MODE_HELP[latest.pccMode]) ?? ''}</div>
        </div>
        {latest.cpuLoad !== null && (
          <div className="stat-chip">
            <div className="stat-chip-label">CPU</div>
            <div className="stat-chip-value">{latest.cpuLoad}%</div>
            <div className="stat-chip-sub">router load</div>
          </div>
        )}
      </div>

      {canWrite && (
        <div className="card">
          <div className="card-header">
            <span className="card-title">Diagnostics</span>
            {diagnosis && !diagnosing && (
              <>
                <Badge intent={VERDICT[diagnosis.verdict].intent}>{VERDICT[diagnosis.verdict].label}</Badge>
                <span className="subtle" style={{ fontSize: 12 }}>
                  {diagnosis.summary} · tested {new Date(diagnosis.ts).toLocaleTimeString()}
                </span>
              </>
            )}
            <span className="grow" />
            {diagnosis && !diagnosing && (
              <button className="btn btn-sm" onClick={() => void suggestWithAi()} disabled={aiLoading}>
                <Sparkles size={13} /> {aiLoading ? 'Thinking…' : aiFixes ? 'Ask AI again' : 'Suggest fixes with AI'}
              </button>
            )}
            <button className="btn btn-sm" onClick={() => void diagnose()} disabled={diagnosing}>
              <Stethoscope size={13} /> {diagnosing ? 'Testing…' : diagnosis ? 'Run again' : 'Run diagnostics'}
            </button>
          </div>

          {diagnosing ? (
            <div className="card-body">
              <Spinner label="Pinging the internet through every line from the router — up to a minute when a line is dead." />
            </div>
          ) : diagError ? (
            <div className="card-body" style={{ fontSize: 13, color: 'var(--itr-critical)' }}>
              {diagError}
            </div>
          ) : !diagnosis ? (
            <div className="card-body subtle" style={{ fontSize: 13 }}>
              Checks whether the internet is unstable and which line is to blame: packet loss, latency and jitter through each
              line, link drops, DHCP leases, cable errors and DNS — and offers a fix where there is one.
            </div>
          ) : (
            <>
              {diagStale && (
                <div style={{ padding: '8px 14px', fontSize: 12.5, borderLeft: '3px solid var(--itr-warning)' }}>
                  Something was changed after this test — run it again to see whether it helped.
                </div>
              )}
              {diagnosis.findings.length === 0 ? (
                <div className="card-body" style={{ fontSize: 13 }}>
                  Nothing wrong found.
                </div>
              ) : (
                <div className="col">
                  {diagnosis.findings.map((f) => (
                    <div
                      key={f.id}
                      className="row"
                      style={{ gap: 12, padding: '10px 14px', alignItems: 'flex-start', borderTop: '1px solid var(--itr-border)' }}
                    >
                      <div style={{ width: 70, flexShrink: 0 }}>
                        <Badge intent={SEVERITY_INTENT[f.severity]}>{f.severity}</Badge>
                      </div>
                      <div className="grow" style={{ minWidth: 0 }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{f.title}</div>
                        <div className="subtle" style={{ fontSize: 12.5, marginTop: 2 }}>
                          {f.detail}
                        </div>
                      </div>
                      <div className="row" style={{ gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        {f.fixes.map((fix) => (
                          <button
                            key={fix.action}
                            className="btn btn-sm"
                            disabled={busy !== null}
                            onClick={() => void act(fix.action, fix.target, fix.confirm)}
                          >
                            <Wrench size={12} /> {busy === `${fix.action}:${fix.target}` ? 'Working…' : fix.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              )}
              {(aiLoading || aiError || aiFixes) && (
                <div style={{ padding: '10px 14px', borderTop: '1px solid var(--itr-border)' }}>
                  <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
                    <Sparkles size={13} style={{ verticalAlign: -2 }} /> AI suggestions
                  </div>
                  {aiLoading ? (
                    <Spinner label="Reading the diagnostics…" />
                  ) : aiError ? (
                    <div style={{ fontSize: 13, color: 'var(--itr-critical)' }}>{aiError}</div>
                  ) : aiFixes ? (
                    <>
                      <div className="subtle" style={{ fontSize: 12.5, marginBottom: 6 }}>
                        {aiFixes.summary}
                      </div>
                      {aiFixes.fixes.length === 0 ? (
                        <div style={{ fontSize: 13 }}>No change on the router is recommended.</div>
                      ) : (
                        aiFixes.fixes.map((fix) => (
                          <div key={`${fix.action}:${fix.target}`} className="row" style={{ gap: 12, padding: '6px 0', alignItems: 'flex-start' }}>
                            <div className="grow" style={{ minWidth: 0 }}>
                              <div style={{ fontSize: 13, fontWeight: 600 }}>{fix.label}</div>
                              <div className="subtle" style={{ fontSize: 12.5 }}>
                                {fix.why}
                              </div>
                            </div>
                            <button
                              className="btn btn-sm"
                              disabled={busy !== null}
                              onClick={() => void act(fix.action, fix.target, fix.confirm)}
                            >
                              <Wrench size={12} /> {busy === `${fix.action}:${fix.target}` ? 'Working…' : 'Apply'}
                            </button>
                          </div>
                        ))
                      )}
                    </>
                  ) : null}
                </div>
              )}
              <div style={{ overflowX: 'auto', borderTop: '1px solid var(--itr-border)' }}>
                <table className="table">
                  <thead>
                    <tr>
                      <th>Line</th>
                      <th title="Pings to a public host, sent through this line only">Internet</th>
                      <th className="nums">Latency</th>
                      <th className="nums" title="Average swing between one reply and the next">
                        Jitter
                      </th>
                      <th title="Pings to the modem this line plugs into">Modem</th>
                      <th className="nums" title="Times the link dropped since the router started">
                        Link drops
                      </th>
                      <th className="nums" title="Damaged frames received + sent since the router started">
                        Errors
                      </th>
                      <th>Negotiated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {diagnosis.lines.map((l) => (
                      <tr key={l.name}>
                        <td className="mono">{l.name}</td>
                        <td>
                          {l.internet ? (
                            <Badge intent={lossIntent(l.internet)}>
                              {l.internet.received === 0 ? 'unreachable' : `${l.internet.lossPct}% loss`}
                            </Badge>
                          ) : (
                            <span className="subtle">not tested</span>
                          )}
                        </td>
                        <td className="nums">{fmtMs(l.internet?.avgMs ?? null)}</td>
                        <td className="nums">{fmtMs(l.internet?.jitterMs ?? null)}</td>
                        <td>
                          {l.gateway ? (
                            <Badge intent={l.gateway.received === 0 ? 'neutral' : lossIntent(l.gateway)}>
                              {l.gateway.received === 0 ? 'no reply' : `${l.gateway.lossPct}% loss`}
                            </Badge>
                          ) : (
                            <span className="subtle">—</span>
                          )}
                        </td>
                        <td className="nums" title={l.lastLinkDown ? `last ${l.lastLinkDown}` : undefined}>
                          {l.linkDowns}
                        </td>
                        <td className="nums">{(l.rxErrors + l.txErrors).toLocaleString()}</td>
                        <td className="mono">
                          {l.rate ? `${l.rate}${l.fullDuplex === false ? ' half' : ''}` : <span className="subtle">—</span>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <span className="card-title">Internet lines</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Port</th>
                <th style={{ width: 80 }}>Link</th>
                <th style={{ width: 130 }} title="Whether the balancer is sending traffic out of this line">
                  Balancer
                </th>
                <th>DHCP</th>
                <th>Gateway</th>
                <th className="nums" title="PCC slots currently leaving through this port">
                  Slots
                </th>
                <th className="nums">Download</th>
                <th className="nums">Upload</th>
                <th title="Last speed-test verdict, from the interface comment. ITR tests every line each hour.">Speed test</th>
                {canWrite && <th style={{ width: 210 }} />}
              </tr>
            </thead>
            <tbody>
              {latest.wans.map((w) => {
                // Dimmed per cell, not per row: the button that brings a line back must not look disabled.
                const dim = w.running ? undefined : { opacity: 0.55 };
                return (
                <tr key={w.name}>
                  <td className="mono" style={dim}>{w.name}</td>
                  <td style={dim}>
                    {w.disabled ? <Badge intent="neutral">off</Badge> : <Badge intent={w.running ? 'good' : 'bad'}>{w.running ? 'up' : 'down'}</Badge>}
                  </td>
                  <td style={dim}>
                    {w.disabled ? (
                      <Badge intent="warn">taken out</Badge>
                    ) : w.slow ? (
                      <Badge intent="warn">pulled out · slow</Badge>
                    ) : w.slotsCarried > 0 ? (
                      <Badge intent="good">in use</Badge>
                    ) : (
                      <Badge intent="neutral">idle</Badge>
                    )}
                  </td>
                  <td className="mono" style={dim}>
                    {w.address ?? <span className="subtle">{w.dhcpStatus ?? '—'}</span>}
                  </td>
                  <td className="mono" style={dim}>{w.gateway ?? <span className="subtle">—</span>}</td>
                  <td className="nums" style={dim}>{w.slotsCarried}</td>
                  <td className="nums" style={dim}>{fmtBps(rate(previous, latest, w.name, 'rxBytes'))}</td>
                  <td className="nums" style={dim}>{fmtBps(rate(previous, latest, w.name, 'txBytes'))}</td>
                  <td className="truncate subtle" style={{ maxWidth: 320, ...dim }} title={w.comment}>
                    {w.comment || '—'}
                  </td>
                  {canWrite && (
                    <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                      {!w.disabled && (
                        <button
                          className="btn btn-sm"
                          style={{ marginRight: 6 }}
                          disabled={busy !== null || !w.running}
                          title={
                            w.running
                              ? "Re-point this line's balancer routes at its current gateway and speed-test it again"
                              : 'The link is down — nothing to speed-test'
                          }
                          onClick={() =>
                            void act(
                              'recalibrate-line',
                              w.name,
                              `Recalibrate ${w.name}?\n\nIts balancer routes are moved to the gateway it has now and its speed is tested with about 10 seconds of download through it. ${
                                w.slow ? 'If it is fast enough it goes back into the balancer.' : 'If it reads slow it is pulled out of the balancer.'
                              } Takes up to half a minute.`,
                            )
                          }
                        >
                          {busy === `recalibrate-line:${w.name}` ? 'Working…' : 'Recalibrate'}
                        </button>
                      )}
                      {w.disabled ? (
                        <button
                          className="btn btn-sm"
                          disabled={busy !== null}
                          onClick={() => void act('enable-line', w.name, `Switch ${w.name} back on?`)}
                        >
                          {busy === `enable-line:${w.name}` ? 'Working…' : 'Put back'}
                        </button>
                      ) : (
                        <button
                          className="btn btn-sm"
                          disabled={busy !== null || lineLock(latest, w.name) !== null}
                          title={lineLock(latest, w.name) ?? 'Switch this line off so its slots fail over to the others'}
                          onClick={() =>
                            void act(
                              'disable-line',
                              w.name,
                              `Switch off ${w.name}?\n\nConnections using it right now drop; its PCC slots fail over to the other lines. It stays off until someone puts it back from this page.`,
                            )
                          }
                        >
                          {busy === `disable-line:${w.name}` ? 'Working…' : 'Take out'}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">PCC slots</span>
          <span className="grow" />
          <span className="subtle" style={{ fontSize: 12 }}>
            which line each share of connections really leaves by
          </span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          <table className="table">
            <thead>
              <tr>
                <th>Mark</th>
                <th>Classifier</th>
                <th>Routing table</th>
                <th>Leaves by</th>
                <th className="nums">Connections</th>
                <th className="nums">Share</th>
                {canWrite && <th style={{ width: 100 }} />}
              </tr>
            </thead>
            <tbody>
              {latest.slots.map((s) => {
                const dim = s.disabled ? { opacity: 0.5 } : undefined;
                return (
                <tr key={s.mark}>
                  <td className="mono" style={dim}>
                    {s.mark}
                    {s.disabled && (
                      <>
                        {' '}
                        <Badge intent="bad">disabled</Badge>
                      </>
                    )}
                  </td>
                  <td className="mono" style={dim}>{s.classifier}</td>
                  <td className="mono" style={dim}>{s.routingTable ?? <span className="subtle">—</span>}</td>
                  <td className="mono" style={dim}>
                    {s.egress ? (
                      <>
                        {s.egress}
                        {s.egressGateway && <span className="subtle"> via {s.egressGateway}</span>}
                      </>
                    ) : (
                      <Badge intent="bad">no active route</Badge>
                    )}
                  </td>
                  <td className="nums" style={dim}>{s.connections.toLocaleString()}</td>
                  <td className="nums" style={dim}>{totalSlotConns ? `${Math.round((s.connections / totalSlotConns) * 100)}%` : '—'}</td>
                  {canWrite && (
                    <td style={{ textAlign: 'right' }}>
                      <button
                        className="btn btn-sm"
                        disabled={busy !== null}
                        onClick={() =>
                          void act(
                            s.disabled ? 'enable-slot' : 'disable-slot',
                            s.mark,
                            s.disabled
                              ? `Enable PCC slot ${s.mark}?`
                              : `Disable PCC slot ${s.mark}?\n\nConnections already open keep their line. New ones that hash to ${s.classifier} stop being balanced and follow the router's main routing table${
                                  latest.slots.filter((x) => !x.disabled).length === 1 ? ' — this is the last enabled slot, so balancing is off entirely' : ''
                                }.`,
                          )
                        }
                      >
                        {busy === `${s.disabled ? 'enable' : 'disable'}-slot:${s.mark}` ? 'Working…' : s.disabled ? 'Enable' : 'Disable'}
                      </button>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Download per line</span>
        </div>
        <div className="card-body">
          <TimeseriesChart series={rxSeries} unit="bits_per_sec" height={220} />
        </div>
      </div>
    </div>
  );
}
